import { AI_CREDIT_CHIP_SX } from "./DataEnrichmentView";
import React, { useMemo } from "react";
import { Box, Button, Chip, ListSubheader, MenuItem, Select, TextField, Typography } from "@mui/material";
import {PaidButtonLabel, SpreadsheetActionNavigation, paidSubmitButtonSx} from "./SpreadsheetActionControls";
import TollIcon from "@mui/icons-material/Toll";
import AiProviderIconStack from "../Workspaces/AiProviderIconStack";
import SuggestedEnrichmentPrompts from "../Workspaces/SuggestedEnrichmentPrompts";
import { PRIMARY_ENRICHMENT_PROMPTS, findSuggestedEnrichmentPromptByText } from "../../helper/suggestedEnrichmentPrompts";
import { AI_PROVIDER_LABELS, AI_PROVIDER_ORDER, SPREADSHEET_AI_MODELS, getSpreadsheetModelById } from "../../helper/aiEnrichment";
import { getProviderIcon } from "./EnrichmentGrid.helpers";
import ActionCreditUsageBanner from "../../components/layouts/ActionCreditUsageBanner";

function UseAiView({
  theme,
  pending = false,
  selectedCount,
  costSuffix = "",
  appliesLabelOverride,
  aiPrompt,
  onAiPromptChange,
  aiModelId,
  onAiModelIdChange,
  onBack,
  onClose,
  onGenerate,
  hasCreditShortfall = false,
  creditBalanceResolved = false,
}) {
  const isInvalidSelection = selectedCount === 0;
  const submitDisabled = pending
    || !aiPrompt.trim()
    || isInvalidSelection
    || !creditBalanceResolved
    || hasCreditShortfall;

  const groupedAiModels = useMemo(() => (
    AI_PROVIDER_ORDER.map((provider) => ({
      provider,
      label: AI_PROVIDER_LABELS[provider] || provider,
      models: SPREADSHEET_AI_MODELS.filter((model) => model.provider === provider),
    })).filter((group) => group.models.length > 0)
  ), []);

  const renderAiModelSelect = ({ fullWidth = true, menuWidth = 440, sx = {} } = {}) => (
    <Select
      value={aiModelId}
      onChange={(e) => onAiModelIdChange(e.target.value)}
      size="small"
      fullWidth={fullWidth}
      MenuProps={{
        PaperProps: {
          style: {
            maxHeight: 380,
            width: menuWidth,
          },
        },
      }}
      sx={{
        bgcolor: 'background.paper',
        '& .MuiSelect-select': { display: 'flex', alignItems: 'center', gap: 1, py: 1 },
        ...sx,
      }}
      renderValue={(selected) => {
        const model = getSpreadsheetModelById(selected);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            {getProviderIcon(model.provider)}
            <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {model.label}
            </Typography>
            <Chip
              size="small"
              label={`${model.credits} credits`}
              sx={{ height: 22, fontSize: '11px', fontWeight: 700, bgcolor: 'rgba(76, 175, 80, 0.1)', color: '#4caf50', flexShrink: 0 }}
            />
          </Box>
        );
      }}
    >
      {groupedAiModels.map((group) => ([
        <ListSubheader
          key={`${group.provider}-header`}
          sx={{
            lineHeight: '32px',
            fontSize: '11px',
            fontWeight: 800,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'text.secondary',
            bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : '#F7F8FA',
          }}
        >
          {group.label}
        </ListSubheader>,
        ...group.models.map((model) => (
          <MenuItem key={model.id} value={model.id} aria-label={`${model.label} ${model.credits} credits`} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', gap: 1.5, pr: 2 }}>
              <Box sx={{ mt: 0.25 }}>{getProviderIcon(model.provider)}</Box>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{model.label}</Typography>
                  {(model.badges || []).map((badge) => (
                    <Chip
                      key={badge}
                      label={badge}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '10px',
                        fontWeight: 600,
                        bgcolor: badge === 'Default'
                          ? 'rgba(76, 175, 80, 0.1)'
                          : theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.1)'
                            : 'rgba(0,0,0,0.06)',
                        color: badge === 'Default' ? '#4caf50' : 'text.secondary',
                      }}
                    />
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, whiteSpace: 'normal', lineHeight: 1.3 }}>
                  {model.description}
                </Typography>
              </Box>
            </Box>
            <Chip
              size="small"
              icon={<TollIcon sx={{ fontSize: '14px !important' }} />}
              label={`${model.credits} credits`}
              sx={AI_CREDIT_CHIP_SX}
            />
          </MenuItem>
        )),
      ]))}
    </Select>
  );

  const appliesLabel = appliesLabelOverride
    || (isInvalidSelection
      ? 'Select at least 1 company.'
      : `Applies to ${selectedCount} selected ${selectedCount === 1 ? 'company' : 'companies'}`);

  const CONTROL_HEIGHT = 40;
  const CONTROL_RADIUS = '10px';

  return (
    <Box

      data-testid="spreadsheet-ai-menu-v4"
      sx={{ width: 520, display: 'flex', flexDirection: 'column' }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 1.75,
          py: 1.1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.15, minWidth: 0 }}>
          <Box
            data-testid="use-ai-header-logo-slot"
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              '--ai-provider-overlap': '-8px',
            }}
          >
            <AiProviderIconStack interactive={false} dense cardSize={24} logoSize={13} />
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 13.5, lineHeight: 1.2, letterSpacing: '-0.01em' }}>
            Use AI Agent
          </Typography>
        </Box>
        <SpreadsheetActionNavigation onBack={onBack} onClose={onClose} />
      </Box>

      <Box
        sx={{
          px: 1.75,
          pt: 1.5,
          pb: 1.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          bgcolor: theme.palette.mode === 'dark' ? 'background.default' : '#FDFDFD',
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4, fontSize: '12.5px' }}>
          Analyze selected companies with their data and web research when needed.
        </Typography>

        <Box data-testid="use-ai-form" sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
          <Box
            sx={{
              border: 1,
              borderColor: isInvalidSelection ? 'error.main' : 'divider',
              borderRadius: CONTROL_RADIUS,
              overflow: 'hidden',
              bgcolor: 'background.paper',
            }}
          >
            <TextField
              value={aiPrompt}
              onChange={(e) => onAiPromptChange(e.target.value)}
              placeholder="E.g., Review this company's website and determine if they are a B2B SaaS provider in the healthcare sector..."
              multiline
              minRows={5}
              fullWidth
              variant="standard"
              InputProps={{
                disableUnderline: true,
                sx: { px: 1.5, py: 1.25, fontSize: '13px', lineHeight: 1.45 },
              }}
              inputProps={{
                'aria-label': 'AI prompt',
                dir: 'ltr',
                style: { textAlign: 'left', direction: 'ltr', unicodeBidi: 'isolate' },
              }}
            />
          </Box>

          <Box data-testid="use-ai-suggested-prompts" sx={{ minWidth: 0, width: '100%' }}>
            <SuggestedEnrichmentPrompts
              prompts={PRIMARY_ENRICHMENT_PROMPTS}
              layout="carousel"
              selectedId={findSuggestedEnrichmentPromptByText(aiPrompt)?.id || null}
              disabled={isInvalidSelection}
              onSelect={(prompt) => {
                if (!prompt) return;
                const currentId = findSuggestedEnrichmentPromptByText(aiPrompt)?.id || null;
                if (currentId === prompt.id) {
                  onAiPromptChange('');
                  return;
                }
                onAiPromptChange(prompt.text);
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, width: '100%' }}>
            <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
              {renderAiModelSelect({
                fullWidth: true,
                menuWidth: 360,
                sx: {
                  height: CONTROL_HEIGHT,
                  '& .MuiOutlinedInput-root': {
                    height: CONTROL_HEIGHT,
                    borderRadius: CONTROL_RADIUS,
                  },
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                    py: 0,
                    height: CONTROL_HEIGHT,
                    boxSizing: 'border-box',
                  },
                },
              })}
            </Box>
            <Button
              variant="contained"
              disableElevation
              onClick={() => {
                if (submitDisabled) return;
                onGenerate();
              }}
              disabled={submitDisabled}
              sx={{...paidSubmitButtonSx(theme, costSuffix), alignSelf: 'center'}}
            >
              <PaidButtonLabel label="Generate" cost={costSuffix} />
            </Button>
          </Box>

          <ActionCreditUsageBanner
            actionId="ai_enrichment"
            selectedCount={selectedCount}
            aiModelId={aiModelId}
          />

          <Typography
            variant="caption"
            color={isInvalidSelection && !appliesLabelOverride ? 'error.main' : 'text.secondary'}
            sx={{
              textAlign: 'right',
              fontWeight: isInvalidSelection && !appliesLabelOverride ? 700 : 400,
              fontSize: '11.5px',
              lineHeight: 1.3,
              width: '100%',
            }}
          >
            {appliesLabel}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export { UseAiView };
